<template>
  <!-- if we're not showing the editor, that means we're opening inside a component. in that case, we don't want it to be a full page -->
  <div class="pt-2">
    <div :class="{ 'contentView': showEditor }">
      <b-tabs>
        <b-tab-item>
          <template #header>
            <b-icon icon="selection-drag"></b-icon>
            <strong>Custom</strong>
          </template>
          <div v-if="type!=='newCustomer'" class="buttons"> <!-- disabled when adding a new patient -->
            <b-button @click="newPlan()" class="is-info">
              <strong>Create a Plan</strong>
            </b-button>
          </div>
          <div v-else class="is-flex mb-4">
            <b-tooltip label="Skip using a template" type="is-black">
              <b-button @click="skipTemplate()" class="is-info">
                <strong>Skip Template</strong>
              </b-button>
            </b-tooltip>
          </div>
            
          <ul v-if="content.templates.custom.length > 0">
            <li v-for="(template, idx) in content.templates.custom" :key="idx" class="templateRow is-clickable" @click="selectTemplate(template, false)">
              <div class="columns">
                <div class="column has-text-left pl-4">
                  <p class="title is-5 mb-2">{{ template.name }}</p>
                  <p>{{ template.weeks.length }} week<span v-if="template.weeks.length > 1">s</span>
                  </p>
                </div>
                <div class="column is-flex is-align-items-center is-justify-content-center">
                  <b-button class="is-info is-light" outlined icon-right="plus-thick">
                    <strong>Select</strong>
                  </b-button>
                </div>
              </div>
            </li>
          </ul>
          <div v-else class="mt-4 has-text-left">
            <p class="subtitle is-5">You haven't saved any custom plans.</p>
          </div>
        </b-tab-item>

        <b-tab-item>
          <template #header>
            <b-icon icon="selection"></b-icon>
            <strong>Default</strong>
          </template>
          <div>
            <ul v-if="content.templates.default.length > 0">
              <li v-for="(template, customIdx) in content.templates.default" :key="customIdx" class="templateRow is-clickable" @click="selectTemplate(template, true)">
                <div class="columns">
                  <div class="column has-text-left pl-4">
                    <p class="title is-5 mb-2">{{ template.name }}</p>
                    <p>{{ template.weeks.length }} week<span v-if="template.weeks.length > 1">s</span></p>
                </div>
                  <div class="column"> <!-- v-if="!showEditor" if the editor is hidden it means we're selecting a plan -->
                    <b-button class="is-info is-light" outlined icon-right="plus-thick">
                      <strong>Select</strong>
                    </b-button>
                  </div>
                </div>
              </li>
            </ul>
            <div v-else>
              <p>No plans here yet!</p>
            </div>
          </div>
        </b-tab-item>
      </b-tabs>
    </div>

    <b-modal v-model="showBuilder" :can-cancel="false" full-screen destroy-on-hide>
      <div class="modal-card">
         <header class="modal-card-head is-flex is-justify-content-center">
          <div class="contentView is-flex is-justify-content-space-between">
            <b-tooltip :active="type!=='viewOnly'" type="is-dark" position="is-right" label="Edit the plan name">
              <div @click="renamePlan()" class="title is-3 mb-0 is-flex is-align-content-center is-clickable">
                <div v-if="selectedTemplate && (type==='editExisting' || type==='viewOnly')">
                  <p>{{ selectedTemplate.name }}</p>
                </div>
                <p v-else>New Plan</p>
                <b-icon v-if="type==='editExisting'" icon="pencil" type="is-info" class="ml-1" />
              </div>
            </b-tooltip>
            <b-button @click="closeBuilder()" class="is-danger is-light" icon-right="close-thick">
              <strong>Cancel</strong>
            </b-button>
          </div>
        </header>
        <section class="modal-card-body">
          <builder />
        </section>
        <footer class="modal-card-foot is-flex is-justify-content-center">
          <div class="contentView buttons is-flex is-justify-content-flex-end">
            <b-button v-if="type!=='viewOnly'" @click="deletePlan()" class="is-danger" icon-right="delete">
              <strong>Delete</strong>
            </b-button>
          </div>
        </footer>
      </div>
    </b-modal>

  </div>
</template>



<script>
import { db } from '@/main';
import { mapGetters, mapState } from 'vuex';
import builder from '@/views/Builder'

export default {
  name: 'templateTabs',
  props: {
    showEditor: { // we manually disable the editor when we add a patient
      type: Boolean,
      default: true,
    },
  },
  components: {
    builder
  },
  computed: {
    ...mapState({
      selectedTemplate: state => state.selectedTemplate,
      type: state => state.builderMenuType,
      hasBeenEdited: state => state.hasPlanEdited,
    }),
    ...mapGetters({
      content: "getAllContent",
    })
  },
  mounted () {
    this.$root.$on('closeSaveConfirm', this.afterConfirm)
  },
  data () {
    return {
      showBuilder: false,
    }
  },
  methods: {
    renamePlan() {
      this.$buefy.dialog.prompt({
        title: `Rename Plan`,
        inputAttrs: {
          placeholder: this.selectedTemplate.name,
        },
        confirmText: 'Rename',
        trapFocus: true,
        onConfirm: (name) => {
          this.$store.commit('RENAME_PLAN', name)
          this.$buefy.toast.open(`Saved`)
        }
      })
    },
    deletePlan() {
      this.$buefy.dialog.confirm({
        title: `Delete ${this.selectedTemplate.name}`,
        message: `Deleting <b>${this.selectedTemplate.name}</b> is permanant and cannot be undone. Continue?`,
        type: 'is-danger',
        onConfirm: () => {
          db.collection('TherapistAccounts').doc(this.$store.state.user.email).collection('customTemplates').doc(this.selectedTemplate.id).delete()
            .then(() => {
              // this.$store.commit('SELECT_TEMPLATE', null)
              this.showBuilder = false;
              this.$buefy.toast.open({
                position: 'is-top',
                message: 'Plan Deleted!'
              })
            })
            .catch(() => {
              this.$buefy.toast.open({
                position: 'is-top',
                message: 'Could not delete plan! Try again.',
                type: 'is-danger'
              })
            })
        }
      })
    },
    afterConfirm() { // must be a method, since being called by bus $on in mounted()
      this.showBuilder = false;
    },
    closeBuilder() {
      if (this.type==='viewOnly' || !this.hasBeenEdited) {
        this.showBuilder = false;
        return;
      }
      this.$buefy.dialog.confirm({
        title: 'Exit Builder',
        message: 'If you exit now, all progress will be lost.',
        type: 'is-danger',
        onConfirm: () => {
          this.showBuilder = false;
        }
      })
    },
    selectTemplate(template, isDefault) { // template is made up of { name: String, weeks: Array }
      const object = { id: template.id, name: template.name, weeks: template.weeks };
      // this.$store.commit('SELECT_TEMPLATE', object)
      if (this.showEditor) { // unless the editor prop has been manually set to false, we open the builder
        if (isDefault) {
          this.$store.commit('SET_BUILDER_BAR_TYPE', 'viewOnly') // default playlists aren't editable
        } else {
          this.$store.commit('SET_BUILDER_BAR_TYPE', 'editExisting')
        }
        this.showBuilder = true;
      } else {
        this.$emit('selectedTemplate'); // lets the "addPatient" modal in Dashboard.vue know to move to customizing
      }
      const send = JSON.parse(JSON.stringify(object)) // force set by value rather than reference, so our local firebase data isn't affected
      this.$store.commit('SELECT_TEMPLATE', send) // commit last, so the type is set before we do anything else
    },
    skipTemplate() {
      this.$store.commit('NEW_TEMPLATE')
      this.$emit('selectedTemplate');
    },
    newPlan() {
      this.$store.commit('SET_BUILDER_BAR_TYPE', 'saveNew')
      this.$store.commit('NEW_TEMPLATE')
      this.showBuilder = true;
    },
  }
}
</script>


<style lang="scss" scoped>
.templateRow {
  margin-bottom: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  background-color: hsl(0, 0%, 93%);
  border: solid 1px hsl(0, 0%, 86%);
  box-shadow: 0px 0px 4px #c1c1c1;
  &:nth-child(odd) {
    background-color: hsl(0, 0%, 98%);
  }
  &:hover {
    box-shadow: 2px 4px 8px #c1c1c1;
  }
}
</style>